// Notification Service for Blood Donation App
// Handles push notifications, location-based matching, and notification preferences
// Uses browser Web Push API (no Firebase dependency)

import { createDocument, updateDocument, useCollection } from '../hooks/useSupabase';
import { getCompatibleBloodGroups } from '../types/schema';

// Notification service class
class NotificationService {
  constructor() {
    this.isSupported = 'Notification' in window && 'serviceWorker' in navigator;
    this.permission = Notification.permission;
  }

  // Request notification permission
  async requestPermission() {
    if (!this.isSupported) {
      console.log('Notifications not supported in this browser');
      return null;
    }

    const permission = await Notification.requestPermission();
    this.permission = permission;
    
    if (permission === 'granted') {
      return await this.getDeviceToken();
    }
    
    console.log('Notification permission denied, continuing without push notifications');
    return null;
  }

  // Get browser push subscription (Web Push API)
  async getDeviceToken() {
    try {
      if (!this.isSupported) {
        console.log('Notifications not supported in this browser');
        return null;
      }
      if (this.permission !== 'granted') {
        console.log('Notification permission not granted');
        return null;
      }
      const registration = await navigator.serviceWorker.ready;
      const vapidKey = import.meta.env.VITE_VAPID_PUBLIC_KEY;
      if (!vapidKey || vapidKey === 'your-vapid-public-key-here') {
        console.log('VAPID key not configured — browser push unavailable');
        return null;
      }
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: this._urlBase64ToUint8Array(vapidKey),
      });
      return JSON.stringify(subscription);
    } catch (error) {
      console.log('Browser push unavailable:', error.message);
      return null;
    }
  }

  _urlBase64ToUint8Array(base64String) {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
    const rawData = window.atob(base64);
    return Uint8Array.from([...rawData].map(c => c.charCodeAt(0)));
  }

  // Register donor for notifications
  async registerDonor(donorId, donorData) {
    try {
      const token = await this.getDeviceToken();
      
      // Always save notification preferences, even without token
      const updateData = {
        notificationPreferences: {
          bloodRequests: true,
          urgentRequests: true,
          compatibleBloodGroups: true,
          locationRadius: donorData.notificationRadius || 50, // km
          enabledMethods: token ? ['push', 'sms'] : ['sms'], // Only SMS if no push token
          quietHours: {
            enabled: false,
            start: '22:00',
            end: '07:00'
          }
        },
        updatedAt: new Date()
      };

      // Add token only if available
      if (token) {
        updateData.fcmToken = token;
        console.log('Donor registered for notifications with push token');
      } else {
        console.log('Donor registered for notifications without push token (SMS only)');
      }

      await updateDocument('donors', donorId, updateData);
      return token;
    } catch (error) {
      console.error('Error registering donor for notifications:', error);
      // Don't throw error - notifications are optional
      console.log('Continuing donor registration without notifications');
      return null;
    }
  }

  // Calculate distance between two coordinates (Haversine formula)
  calculateDistance(lat1, lon1, lat2, lon2) {
    const R = 6371; // Earth's radius in kilometers
    const dLat = this.toRad(lat2 - lat1);
    const dLon = this.toRad(lon2 - lon1);
    const a = 
      Math.sin(dLat/2) * Math.sin(dLat/2) +
      Math.cos(this.toRad(lat1)) * Math.cos(this.toRad(lat2)) * 
      Math.sin(dLon/2) * Math.sin(dLon/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return R * c;
  }

  toRad(value) {
    return value * Math.PI / 180;
  }

  // Find eligible donors for a blood request
  async findEligibleDonors(bloodRequest, allDonors) {
    const eligibleDonors = [];
    const compatibleGroups = getCompatibleBloodGroups(bloodRequest.bloodGroup);
    
    // Hospital coordinates (you might want to store these in hospital data)
    const hospitalCoordinates = this.getHospitalCoordinates(bloodRequest.hospital);
    
    for (const donor of allDonors) {
      // Check if donor is available and has compatible blood group
      if (!donor.isAvailable || !compatibleGroups.includes(donor.bloodGroup)) {
        continue;
      }

      // Check notification preferences
      if (!donor.notificationPreferences?.bloodRequests) {
        continue;
      }

      // Check if donor has FCM token
      if (!donor.fcmToken) {
        continue;
      }

      // Location-based filtering
      const donorCoordinates = this.getDonorCoordinates(donor);
      if (hospitalCoordinates && donorCoordinates) {
        const distance = this.calculateDistance(
          hospitalCoordinates.lat, hospitalCoordinates.lng,
          donorCoordinates.lat, donorCoordinates.lng
        );
        
        const maxRadius = donor.notificationPreferences?.locationRadius || 50;
        if (distance > maxRadius) {
          continue;
        }
      } else {
        // Fallback to district matching if coordinates not available
        if (donor.district !== bloodRequest.hospital.district) {
          continue;
        }
      }

      // Check quiet hours
      if (this.isQuietHours(donor.notificationPreferences?.quietHours)) {
        continue;
      }

      eligibleDonors.push({
        ...donor,
        distance: hospitalCoordinates && donorCoordinates ? 
          this.calculateDistance(
            hospitalCoordinates.lat, hospitalCoordinates.lng,
            donorCoordinates.lat, donorCoordinates.lng
          ) : null
      });
    }

    // Sort by distance (closest first)
    return eligibleDonors.sort((a, b) => {
      if (a.distance && b.distance) {
        return a.distance - b.distance;
      }
      return 0;
    });
  }

  // Get hospital coordinates (mock data - you should store real coordinates)
  getHospitalCoordinates(hospital) {
    // This is a simplified version - you should store actual coordinates
    const hospitalCoords = {
      'Thiruvananthapuram': { lat: 8.5241, lng: 76.9366 },
      'Kochi': { lat: 9.9312, lng: 76.2673 },
      'Kozhikode': { lat: 11.2588, lng: 75.7804 },
      'Thrissur': { lat: 10.5276, lng: 76.2144 },
      // Add more hospital coordinates
    };
    
    return hospitalCoords[hospital.district] || null;
  }

  // Get donor coordinates (mock data - you should implement geolocation)
  getDonorCoordinates(donor) {
    // This is a simplified version - you should store actual coordinates
    const districtCoords = {
      'Thiruvananthapuram': { lat: 8.5241, lng: 76.9366 },
      'Ernakulam': { lat: 9.9312, lng: 76.2673 },
      'Kozhikode': { lat: 11.2588, lng: 75.7804 },
      'Thrissur': { lat: 10.5276, lng: 76.2144 },
      // Add more district coordinates
    };
    
    return districtCoords[donor.district] || null;
  }

  // Check if current time is within quiet hours
  isQuietHours(quietHoursConfig) {
    if (!quietHoursConfig?.enabled) return false;
    
    const now = new Date();
    const currentTime = now.getHours() * 60 + now.getMinutes();
    
    const [startHour, startMin] = quietHoursConfig.start.split(':').map(Number);
    const [endHour, endMin] = quietHoursConfig.end.split(':').map(Number);
    
    const startTime = startHour * 60 + startMin;
    const endTime = endHour * 60 + endMin;
    
    if (startTime <= endTime) {
      return currentTime >= startTime && currentTime <= endTime;
    } else {
      // Quiet hours cross midnight
      return currentTime >= startTime || currentTime <= endTime;
    }
  }

  // Send notification to eligible donors
  async notifyDonors(bloodRequest, eligibleDonors) {
    const notifications = [];
    
    for (const donor of eligibleDonors) {
      try {
        const notificationData = {
          donorId: donor.id,
          requestId: bloodRequest.id,
          type: 'blood_request',
          title: `🩸 Urgent: ${bloodRequest.bloodGroup} Blood Needed`,
          body: `${bloodRequest.patientName} needs ${bloodRequest.unitsRequired} units at ${bloodRequest.hospital.name}, ${bloodRequest.hospital.district}`,
          data: {
            requestId: bloodRequest.id,
            bloodGroup: bloodRequest.bloodGroup,
            hospital: bloodRequest.hospital.name,
            district: bloodRequest.hospital.district,
            urgency: bloodRequest.urgencyLevel,
            distance: donor.distance ? `${Math.round(donor.distance)}km away` : 'Same district'
          },
          fcmToken: donor.fcmToken,
          sentAt: new Date(),
          status: 'pending'
        };

        // Store notification in Firestore for tracking
        const notificationId = await createDocument('notifications', notificationData);
        
        // Send push notification (this would typically be done via Cloud Functions)
        await this.sendPushNotification(notificationData);
        
        notifications.push({ ...notificationData, id: notificationId });
        
      } catch (error) {
        console.error(`Error sending notification to donor ${donor.id}:`, error);
      }
    }
    
    return notifications;
  }

  // Send push notification (mock implementation - use Cloud Functions in production)
  async sendPushNotification(notificationData) {
    // In production, this should be handled by Firebase Cloud Functions
    // This is a client-side mock for demonstration
    
    if (this.permission === 'granted') {
      // Show browser notification as fallback
      const notification = new Notification(notificationData.title, {
        body: notificationData.body,
        icon: '/icon-192x192.png',
        badge: '/icon-192x192.png',
        tag: `blood-request-${notificationData.requestId}`,
        data: notificationData.data,
        actions: [
          {
            action: 'accept',
            title: 'I can donate',
            icon: '/icons/heart.png'
          },
          {
            action: 'view',
            title: 'View details',
            icon: '/icons/view.png'
          }
        ]
      });

      notification.onclick = () => {
        window.focus();
        window.location.href = `/requests?highlight=${notificationData.requestId}`;
        notification.close();
      };
    }
  }

  // No-op: foreground message handling is done via service worker postMessage
  setupMessageListener() {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', (event) => {
        if (event.data?.type === 'BLOOD_REQUEST_NOTIFICATION') {
          console.log('Push notification data received in foreground:', event.data);
        }
      });
    }
  }

  // Update notification preferences
  async updateNotificationPreferences(donorId, preferences) {
    try {
      await updateDocument('donors', donorId, {
        notificationPreferences: preferences,
        updatedAt: new Date()
      });
      
      console.log('Notification preferences updated');
      return true;
    } catch (error) {
      console.error('Error updating notification preferences:', error);
      throw error;
    }
  }
}

// Export singleton instance
export const notificationService = new NotificationService();

// Hook to get notification history
export const useNotificationHistory = (donorId) => {
  return useCollection('notifications', donorId ? [['donor_id', '==', donorId]] : []);
};

// Utility function to trigger notifications for new blood requests
export const triggerBloodRequestNotifications = async (bloodRequest, allDonors) => {
  try {
    const eligibleDonors = await notificationService.findEligibleDonors(bloodRequest, allDonors);
    
    if (eligibleDonors.length > 0) {
      console.log(`Found ${eligibleDonors.length} eligible donors for notification`);
      const notifications = await notificationService.notifyDonors(bloodRequest, eligibleDonors);
      
      // Log notification summary
      await createDocument('notificationSummaries', {
        requestId: bloodRequest.id,
        totalEligible: eligibleDonors.length,
        notificationsSent: notifications.length,
        bloodGroup: bloodRequest.bloodGroup,
        hospital: bloodRequest.hospital,
        urgencyLevel: bloodRequest.urgencyLevel,
        createdAt: new Date()
      });
      
      return notifications;
    }
    
    return [];
  } catch (error) {
    console.error('Error triggering blood request notifications:', error);
    throw error;
  }
};

export default notificationService;
